<template>
    <client-only>
      <div v-if="canRenderWorkspace" class="translate-page">
        <div class="translate-container">
          <!-- 左侧导航 -->
          <div class="translate-left">
            <TranslateSidebar
              :active-menu="activeMenu"
              @menuChange="handleMenuChange"
            />
          </div>

          <!-- 右侧内容区域 -->
          <div class="translate-right">
            <keep-alive>
              <component :is="currentComponent" />
            </keep-alive>
          </div>
        </div>
      </div>

      <div v-else class="enterprise-access-pending" />
      <EnterpriseWorkspaceAccessDialog
        v-if="!canRenderWorkspace && !isLoginDrawerOpen && !accessDialogDismissed && (accessState === 'guest' || accessState === 'registered')"
        :mode="accessState"
        @secondary="handleAccessSecondary"
        @plans="handleViewPlans"
        @link="handleAccessLink"
      />
    </client-only>
  </template>
  
  <script setup lang="ts">
  import TranslateSidebar from '~/components/Translate/TranslateSidebar/index.vue'
  import EventBus from 'vue3-eventbus'
  import useUserStore from '~/store/modules/user'
  import { storeToRefs } from 'pinia'
  import type LogoutDialog from '~/components/LogoutDialog/index.vue'
  import EnterpriseWorkspaceAccessDialog from '~/components/Translate/EnterpriseWorkspaceAccessDialog.vue'
  import { hasPendingEnterpriseInvitation } from '~/utils/enterpriseManagement'
  import { canRenderEnterpriseWorkspace, resolveEnterpriseWorkspaceAccess } from '~/utils/enterpriseWorkspaceAccess'
  definePageMeta({
    layout: 'workbenches'
  })
  
  // 菜单类型定义
  type MenuType = 'home' | 'image' | 'enterprise' | 'records' | 'consumption' | 'glossary' | 'orders' | 'user' | 'combo' | 'support'
  
  // 当前激活的菜单
  const activeMenu = ref<MenuType>('home')
  
  // 组件映射
  const components = {
    home: defineAsyncComponent(() => import('~/components/Translate/Home/index.vue')),
    image: defineAsyncComponent(() => import('~/components/Translate/Image/index.vue')),
    enterprise: defineAsyncComponent(() => import('~/components/Translate/Enterprise/index.vue')),
    records: defineAsyncComponent(() => import('~/components/Translate/Records/index.vue')),
    consumption: defineAsyncComponent(() => import('~/components/Translate/Consumption/index.vue')),
    glossary: defineAsyncComponent(() => import('~/components/Translate/Glossary/index.vue')),
    orders: defineAsyncComponent(() => import('~/components/Translate/Orders/index.vue')),
    combo: defineAsyncComponent(() => import('~/components/Translate/Combo/index.vue')),
    support: defineAsyncComponent(() => import('~/components/Translate/PrioritySupport/index.vue')),
    user: defineAsyncComponent(() => import('~/components/Translate/User/index.vue'))
  }
  
  // 当前组件
  const currentComponent = computed(() => components[activeMenu.value])
  
  // 路由相关
  const route = useRoute()
  const router = useRouter()
  const localePath = useLocalePath()
  const userStore = useUserStore()
  const { userInfo, token, isUserInfoReady } = storeToRefs(userStore)
  const { $loginCom } = useNuxtApp()
  const isLoginDrawerOpen = $loginCom.isOpen
  const logoutDialogRef = inject('logoutDialogRef') as Ref<InstanceType<typeof LogoutDialog> | undefined>
  const accessDialogDismissed = ref(false)
  const accessState = computed(() => resolveEnterpriseWorkspaceAccess(
    token.value,
    isUserInfoReady.value,
    userInfo.value?.bizTeamId,
  ))
  const hasPendingInvite = computed(() => hasPendingEnterpriseInvitation(userInfo.value))
  const canRenderWorkspace = computed(() => canRenderEnterpriseWorkspace(
    accessState.value,
    activeMenu.value,
    hasPendingInvite.value,
  ))

  watch(() => userInfo.value?.bizTeamId, (newBizTeamId, oldBizTeamId) => {
    if (newBizTeamId && oldBizTeamId !== newBizTeamId) {
      getEnterpriseInfo()
    }
  })

  // 处理菜单切换
  const handleMenuChange = (menuType: MenuType) => {
    activeMenu.value = menuType
    // 更新路由
    router.push({
      query: { 
        menu: menuType 
      }
    })
  }
  
  // 监听路由变化
  watchEffect(() => {
    const menuFromRoute = route.query.menu as MenuType
    if (menuFromRoute && menuFromRoute in components) {
      activeMenu.value = menuFromRoute
    }
  })
  

  function getEnterpriseInfo(){
    if (accessState.value !== 'authorized') return
    userStore.getBizTeamInfo()
  }

  function goHome() {
    router.push(localePath('/'))
  }

  function handleViewPlans() {
    router.push({ path: localePath('/'), hash: '#pro-view' })
  }

  function handleAccessSecondary() {
    if (accessState.value === 'guest') {
      $loginCom.open()
      return
    }
    goHome()
  }

  function handleAccessLink() {
    if (accessState.value === 'guest') {
      goHome()
      return
    }
    logoutDialogRef.value?.open(() => {
      accessDialogDismissed.value = true
    })
  }

  const handleWindowMessage = (event: MessageEvent) => {
    const receivedMessage = event.data
    if (receivedMessage && receivedMessage.refresh === true) {
      userStore.getUserInfo()
      if (accessState.value === 'authorized') userStore.getBizTeamInfo()
    }
  }

  // 页面初始化时设置默认菜单
  onMounted(() => {
    if (!route.query.menu) {
      activeMenu.value = 'home'
    }

    // 这里需要请求企业信息
    if (accessState.value === 'authorized') {
      getEnterpriseInfo()
    }

    // 第二个窗口
    window.addEventListener('message', handleWindowMessage)
    // 创建或加入企业调用
    EventBus.on('createAccountEnterprise', getEnterpriseInfo)
  })

  onUnmounted(()=>{
    EventBus.off('createAccountEnterprise', getEnterpriseInfo)
    window.removeEventListener('message', handleWindowMessage)
  })

  </script>
  
  <style lang="scss" scoped>
  .translate-page {
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: #f5f5f5;
  }

  .enterprise-access-pending {
    width: 100%;
    height: 100%;
    background: #fff;
  }
  
  .translate-container {
    display: flex;
    height: 100%;
  }
  
  .translate-left {
    width: 184px;
    background-color: #fff;
    border-right: 1px solid #e8e8e8;
    flex-shrink: 0;
  }
  
  .translate-right {
    flex: 1;
    background-color: #fff;
    padding: 20px 28px;
    overflow-y: auto;
    scrollbar-gutter: stable;
    display: flex;
    flex-direction: column;
  }
  </style>
